import { apiErrorMessage } from '@/lib'
import { Button, emitUnhandledError, logger, RichLiveField, TemplatePicker } from '@/ui'
import { useBulkCandidateEmailStore } from '@jobilla/nexus-sdk'
import { CircleCheckIcon, SendIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stripHtml } from '../candidate-card/email-thread/strip-html'

export interface BulkEmailModalProps {
	isOpen: boolean
	onClose: () => void
	candidateIds: Set<string>
	campaignId: string
	onSuccess: () => void
}

export function BulkEmailModal({
	isOpen,
	onClose,
	candidateIds,
	campaignId,
	onSuccess,
}: BulkEmailModalProps) {
	const { t } = useTranslation('common')
	const [subject, setSubject] = useState('')
	const [body, setBody] = useState('')
	const [sent, setSent] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	const { mutateAsync: sendBulkEmail, isPending: isSending } = useBulkCandidateEmailStore({
		mutationKey: ['bulk-candidate-email'],
	})

	const candidateCount = candidateIds.size
	const recipientLabel = t('bulkEmail.candidateCount', { count: candidateCount })
	const canSend = candidateCount > 0 && stripHtml(body).trim().length > 0

	const resetAndClose = useCallback(() => {
		setSubject('')
		setBody('')
		setSent(false)
		onClose()
	}, [onClose])

	const handleDone = useCallback(() => {
		resetAndClose()
		onSuccess()
	}, [resetAndClose, onSuccess])

	const handleSend = useCallback(async () => {
		if (!canSend || isSending) { return }

		try {
			await sendBulkEmail({
				data: {
					campaignId: campaignId,
					candidateIds: Array.from(candidateIds),
					subject: subject.trim() || null,
					body,
				},
			})
			setSent(true)
		} catch (err) {
			logger.error(err, {
				tags: { component: 'BulkEmailModal', action: 'send' },
			})
			emitUnhandledError({
				title: t('bulkEmail.errorTitle'),
				description: apiErrorMessage(err) ?? t('bulkEmail.errorDefault'),
				cause: err,
			})
		}
	}, [canSend, isSending, sendBulkEmail, campaignId, candidateIds, subject, body])

	useEffect(() => {
		if (!isOpen || sent) { return }

		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSend && !isSending) {
				e.preventDefault()
				void handleSend()
			}
		}

		const container = containerRef.current
		if (container) {
			container.addEventListener('keydown', handleKeyDown)
			return () => container.removeEventListener('keydown', handleKeyDown)
		}
	}, [isOpen, sent, canSend, isSending, handleSend])

	if (!isOpen) { return null }

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div className="absolute inset-0 bg-black/50" onClick={sent ? handleDone : resetAndClose} />

			{sent
				? (
					<div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-8 flex flex-col items-center text-center">
						<div className="size-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
							<CircleCheckIcon className="size-6 text-green-600" />
						</div>
						<h2 className="text-lg font-semibold text-gray-900 mb-1">
							{t('bulkEmail.successTitle')}
						</h2>
						<p className="text-sm text-gray-500 mb-6">
							{t('bulkEmail.successDescription', { count: candidateCount })}
						</p>
						<Button type="committal" size="short" onClick={handleDone}>
							{t('bulkEmail.gotIt')}
						</Button>
					</div>
				)
				: (
					<div
						ref={containerRef}
						className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[80vh]"
					>
						<div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
							<div className="flex items-center gap-2">
								<SendIcon className="size-4 text-brand-600" />
								<h2 className="text-sm font-medium text-gray-900">
									{t('bulkEmail.sendingTo', { recipients: recipientLabel })}
								</h2>
								<TemplatePicker
									onSelect={(template) => {
										if (template.subject) { setSubject(template.subject) }
										setBody(template.body)
									}}
								/>
							</div>
							<button
								type="button"
								onClick={resetAndClose}
								aria-label={t('common.dismiss')}
								className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
							>
								<XIcon className="size-4" />
							</button>
						</div>

						<div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
							<div className="flex items-baseline gap-3">
								<label
									htmlFor="bulk-email-subject"
									className="text-sm font-medium text-gray-500 w-16 flex-shrink-0"
								>
									{t('bulkEmail.subjectLabel')}
								</label>
								<input
									id="bulk-email-subject"
									type="text"
									value={subject}
									onChange={(e) => setSubject(e.target.value)}
									placeholder={t('bulkEmail.subjectPlaceholder')}
									className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
								/>
							</div>

							<div className="flex flex-col border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all min-h-[200px]">
								<RichLiveField
									value={body}
									onChange={setBody}
									outputFormat="html"
									disableMedia
									showStaticToolbar
								/>
							</div>
						</div>

						<div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30 flex-shrink-0">
							<div className="text-xs text-gray-400">
								Press{' '}
								<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">
									Cmd/Ctrl
								</kbd>
								+
								<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-[10px]">
									Enter
								</kbd>{' '}
								to send
							</div>
							<div className="flex items-center gap-3">
								<Button type="subtle" size="short" onClick={resetAndClose}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									size="short"
									onClick={handleSend}
									disabled={!canSend}
									isLoading={isSending}
								>
									<span className="flex items-center gap-2">
										<SendIcon className="size-4" />
										{t('bulkEmail.sendTo', { recipients: recipientLabel })}
									</span>
								</Button>
							</div>
						</div>
					</div>
				)}
		</div>
	)
}
